import { type ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import { BusinessArea, PropertyType } from '@ev-tech/search-modules-api';

import { getPropertySubTypeKey, getPropertyTypeKey } from './translationKeyUtils';
import {
  getSelectedPropertyTypes,
  getSelectedSubTypes,
  type ObjectTypeCategory,
  type PropertyTypeState,
  type PropertyTypeEntry,
} from '../hooks/stores/search/slices/objectTypeFilterSlice';

const residentialPropertyTypes = [PropertyType.house, PropertyType.apartment, PropertyType.land] as const;

export const getCountFromPropertyType = (propertyTypeObject: PropertyTypeEntry): number => {
  if (propertyTypeObject.selected) {
    return 1;
  }
  if (propertyTypeObject.subTypes) {
    return Object.values(propertyTypeObject.subTypes).filter((subType) => subType.selected).length;
  }
  return 0;
};

export const getDisplayedCount = (filterState: PropertyTypeState): number => {
  let totalDisplayedCount = 0;
  for (const propertyTypeObject of Object.values(filterState.commercial)) {
    totalDisplayedCount += getCountFromPropertyType(propertyTypeObject);
  }
  if (totalDisplayedCount === 0) {
    let overrideCount = 0;
    for (const residentialPropertyType of residentialPropertyTypes) {
      const propertyTypeObject = filterState.residential[residentialPropertyType];
      if (propertyTypeObject?.selected) {
        overrideCount += 1;
      }
      if (propertyTypeObject?.subTypes) {
        totalDisplayedCount += getCountFromPropertyType(propertyTypeObject);
      }
    }
    if (overrideCount === residentialPropertyTypes.length) {
      totalDisplayedCount = 0;
    }
  }
  return totalDisplayedCount;
};

export const getObjectTypeFilterButtonLabel = ({
  propertyTypeState,
  objectTypeCategory,
  displayedFilterCount,
  t,
}: {
  propertyTypeState: PropertyTypeState;
  objectTypeCategory: ObjectTypeCategory;
  displayedFilterCount: number;
  t: ComponentTFunction;
}) => {
  if (displayedFilterCount === 0) {
    return objectTypeCategory === BusinessArea.residential
      ? t('search.searchbar.objectType')
      : t(`search.searchbar.advFilter.propertyType.${objectTypeCategory}`);
  }

  const selectedPropertyTypes = getSelectedPropertyTypes(propertyTypeState, objectTypeCategory);
  const selectedPropertySubTypes = getSelectedSubTypes(propertyTypeState);

  if (selectedPropertyTypes.length > 0 && selectedPropertyTypes[0]) {
    const propertyTypeKey = getPropertyTypeKey(selectedPropertyTypes[0]);
    return propertyTypeKey ? t(propertyTypeKey) : '';
  }

  if (selectedPropertySubTypes.length > 0 && selectedPropertySubTypes[0]) {
    const propertySubTypeKey = getPropertySubTypeKey(selectedPropertySubTypes[0]);
    return propertySubTypeKey ? t(propertySubTypeKey) : '';
  }

  // Fallback when state is inconsistent (e.g., during mode switching)
  return objectTypeCategory === BusinessArea.residential
    ? t('search.searchbar.objectType')
    : t(`search.searchbar.advFilter.propertyType.${objectTypeCategory}`);
};
